import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useReducer,
} from "react";
import {
	ToastProvider as RadixToastProvider,
	Toast,
	ToastClose,
	ToastDescription,
	ToastTitle,
	ToastViewport,
} from "../primitives/Toast";

// --- Types ---

export type ToastTone = "success" | "error" | "info";

export interface ToastOptions {
	description?: string;
}

interface ToastEntry {
	id: string;
	tone: ToastTone;
	title: string;
	description?: string;
	open: boolean;
	/** Duration in ms before auto-dismiss. */
	duration: number;
}

// --- Reducer ---

type ToastAction =
	| { type: "ADD"; entry: ToastEntry }
	| { type: "DISMISS"; id: string }
	| { type: "REMOVE"; id: string };

function toastReducer(state: ToastEntry[], action: ToastAction): ToastEntry[] {
	switch (action.type) {
		case "ADD":
			return [...state, action.entry];
		case "DISMISS":
			return state.map((t) => (t.id === action.id ? { ...t, open: false } : t));
		case "REMOVE":
			return state.filter((t) => t.id !== action.id);
	}
}

// --- Context ---

interface ToastContextValue {
	toast: {
		success: (title: string, opts?: ToastOptions) => void;
		error: (title: string, opts?: ToastOptions) => void;
		info: (title: string, opts?: ToastOptions) => void;
	};
	dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// --- Provider ---

let IdCounter = 0;

function nextId(): string {
	IdCounter += 1;
	return `toast-${IdCounter}`;
}

const DURATION: Record<ToastTone, number> = {
	success: 5000,
	error: 8000,
	info: 5000,
};

export interface AppToastProviderProps {
	children: ReactNode;
}

/**
 * Application-level toast provider. Wrap the root of the app with this
 * to enable imperative toasts via the `useToast` hook.
 *
 * Named `AppToastProvider` to avoid collision with the Radix-level
 * `ToastProvider` re-exported from `primitives/Toast`.
 */
function AppToastProvider({ children }: AppToastProviderProps): ReactNode {
	const [toasts, dispatch] = useReducer(toastReducer, []);

	const add = useCallback(
		(tone: ToastTone, title: string, opts?: ToastOptions) => {
			const entry: ToastEntry = {
				id: nextId(),
				tone,
				title,
				description: opts?.description,
				open: true,
				duration: DURATION[tone],
			};
			dispatch({ type: "ADD", entry });
		},
		[],
	);

	const dismiss = useCallback((id: string) => {
		dispatch({ type: "DISMISS", id });
	}, []);

	const value: ToastContextValue = {
		toast: {
			success: (title, opts) => add("success", title, opts),
			error: (title, opts) => add("error", title, opts),
			info: (title, opts) => add("info", title, opts),
		},
		dismiss,
	};

	return (
		<ToastContext.Provider value={value}>
			<RadixToastProvider swipeDirection="right">
				{children}
				<ToastRenderer
					toasts={toasts}
					onOpenChange={(id, open) => {
						if (!open) {
							dispatch({ type: "DISMISS", id });
						}
					}}
					onAnimationEnd={(id) => dispatch({ type: "REMOVE", id })}
				/>
				<ToastViewport />
			</RadixToastProvider>
		</ToastContext.Provider>
	);
}
AppToastProvider.displayName = "AppToastProvider";

// --- Internal toast renderer ---

const TONE_BORDER: Record<ToastTone, string> = {
	success: "border-statusSuccess/40",
	error: "border-destructive/40",
	info: "border-statusInfo/40",
};

interface ToastRendererProps {
	toasts: ToastEntry[];
	onOpenChange: (id: string, open: boolean) => void;
	onAnimationEnd: (id: string) => void;
}

function ToastRenderer({
	toasts,
	onOpenChange,
	onAnimationEnd,
}: ToastRendererProps): ReactNode {
	return (
		<>
			{toasts.map((t) => (
				<Toast
					key={t.id}
					open={t.open}
					duration={t.duration}
					onOpenChange={(open) => onOpenChange(t.id, open)}
					onAnimationEnd={() => {
						if (!t.open) {
							onAnimationEnd(t.id);
						}
					}}
					className={TONE_BORDER[t.tone]}
				>
					<div className="grid gap-1">
						<ToastTitle>{t.title}</ToastTitle>
						{t.description && (
							<ToastDescription>{t.description}</ToastDescription>
						)}
					</div>
					<ToastClose />
				</Toast>
			))}
		</>
	);
}
ToastRenderer.displayName = "ToastRenderer";

// --- Hook ---

export function useToast(): ToastContextValue {
	const ctx = useContext(ToastContext);
	if (ctx === null) {
		throw new Error("useToast must be used within a AppToastProvider");
	}
	return ctx;
}

export default AppToastProvider;
export { AppToastProvider };
